/**
 * @file The read that reattaches a table to the fetch it was following before the tab closed.
 * Consumer: `useSignifyFetchJobModal`, on every change of `enabled`, `targetKey` or `userId` while
 * that table holds no job pointer. Nothing keeps it to one run per mount, and nothing needs to: the
 * effect skips a table that already has a key, and the caller re-checks that key after the read
 * resolves, so a repeat costs a query and can never overwrite a pointer another writer has set.
 * The query asks for the operator's live AND completed jobs newest first and the `targetKey` match
 * is made on the client: an operator's jobs are serialised, a job document lives seven days, and
 * twenty documents cover far more than one operator writes in that time, so `targetKey` needs no
 * index of its own. A live match wins outright; only when every match is `completed` is the newest
 * one probed.
 * A completed job is reattached only when it still holds a `done` part with no `insertedAt` — those
 * parts cost real vendor calls and are stranded until the TTL reaps them otherwise. The probe is a
 * `limit(1)` query on that job's parts, so the whole decision costs one extra document read, and
 * only while the table has no fetch to show; bounding by `createdAt` instead would cost nothing but
 * would put a fully inserted job in front of the operator every time they opened the modal, which
 * is the one outcome worth paying a read to avoid. It carries `userId` because `firestore.rules` admits a list on
 * `parts` only for the owner, exactly as the parts listener does.
 * A job the operator dismissed with "New list" is skipped for good: the pointer that dismissal
 * clears lives in memory, so without `dismissedSignifyFetchJobs` a reload would reattach the very
 * job they let go of.
 */

import { collection, getDocs, limit, orderBy, query, where } from 'firebase/firestore';
import { SIGNIFY_FETCH_JOBS_COLLECTION, signifyFetchJobPartsPath } from '#types';
import type { SignifyFetchJob, SignifyFetchJobPhase } from '#types';
import { firestore } from '@/services/startFirestore';
import { dismissedSignifyFetchJobs } from './dismissedSignifyFetchJobs';

const REATTACH_SCAN_LIMIT = 20;

const REATTACHABLE_PHASES: SignifyFetchJobPhase[] = ['queued', 'running', 'completed'];

const hasPartsWorthInserting = async (jobId: string, userId: string): Promise<boolean> => {
  const parts = await getDocs(
    query(
      collection(firestore, signifyFetchJobPartsPath(jobId)),
      where('userId', '==', userId),
      where('state', '==', 'done'),
      where('insertedAt', '==', null),
      limit(1),
    ),
  );
  return !parts.empty;
};

export const findReattachableSignifyFetchJob = async (
  userId: string,
  targetKey: string,
): Promise<SignifyFetchJob | null> => {
  const snapshot = await getDocs(
    query(
      collection(firestore, SIGNIFY_FETCH_JOBS_COLLECTION),
      where('userId', '==', userId),
      where('phase', 'in', REATTACHABLE_PHASES),
      orderBy('createdAt', 'desc'),
      limit(REATTACH_SCAN_LIMIT),
    ),
  );

  const matches = snapshot.docs.filter(
    (document) =>
      document.get('targetKey') === targetKey && !dismissedSignifyFetchJobs.has(document.id),
  );
  const live = matches.find((document) => document.get('phase') !== 'completed');
  const candidate = live ?? matches[0];
  if (candidate === undefined) return null;
  if (live === undefined && !(await hasPartsWorthInserting(candidate.id, userId))) return null;

  return { ...candidate.data(), docId: candidate.id } as SignifyFetchJob;
};
